import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import {
  GeneralSandbox,
  AuthSandbox,
  NavigationService,
  utils,
  RoutesEnum, GroupedSelectOptions, SelectOption
} from '../../../modules/global';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {environment} from '../../../environments/environment';
import { debounceTime, shareReplay, takeUntil, tap } from 'rxjs/operators';
import {CurrencyTransformPipe} from '../../../modules/shared';
import { TranslateService, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import { GeneralPackage, PackageListResponse } from '../../../modules/global/entities/package.entity';
import { NgIf, AsyncPipe } from '@angular/common';
import { ExternalPageContainerComponent } from '../../components/externap-page-container/external-page-container.component';
import { RegistrationForm } from '../../../modules/shared/containers/registration/registration.form';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha-2';
import { ButtonComponent } from '../../../modules/shared/components/buttons/button/button.component';

@Component({
    selector: 'slm-registration-container',
    templateUrl: './registration.container.html',
    providers: [CurrencyTransformPipe],
    imports: [NgIf, ExternalPageContainerComponent, TranslateDirective, RegistrationForm, RecaptchaModule, RecaptchaFormsModule, FormsModule, ReactiveFormsModule, ButtonComponent, AsyncPipe, TranslatePipe]
})
export class RegistrationContainer implements OnInit, OnDestroy {
  private readonly navigation = inject(NavigationService);
  private readonly currencyPipe = inject(CurrencyTransformPipe);
  private readonly generalSandbox = inject(GeneralSandbox);
  private readonly authSandbox = inject(AuthSandbox);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);

  private readonly destroy$ = new Subject();
  private packages$: Observable<PackageListResponse>;

  public readonly isLoading$ = new BehaviorSubject<boolean>(false);
  public readonly recaptchaSiteKey: string = environment.recaptchaSiteKey;

  public readonly routes = RoutesEnum;

  public registrationForm: UntypedFormGroup;

  public readonly recaptchaControl = new UntypedFormControl('', [Validators.required]);
  public subscriptionOptions = signal<Array<GroupedSelectOptions>>([]);
  public storageSubscriptionOptions = signal<Array<SelectOption>>([]);

  public showSuccessScreen = false;
  public email = '';
  public defaultPackage: string;
  public useDefaultPackage: GeneralPackage;

  ngOnInit() {
    /**
     * Loading and building subscription package list
     * Example [{label: 'BASIC - 49,99 EUR + VAT'}, ...]
     */
    this.defaultPackage = (this.route.snapshot.queryParams['package'] || '').toUpperCase();

    this.packages$ = this.generalSandbox.getSubscriptions().pipe(
      debounceTime(1000),
      shareReplay({refCount: true, bufferSize: 2})
    );

    this.packages$.pipe(
      tap(packages => {
        this.useDefaultPackage = (packages?.generalPackages || []).find((item) => item.name === this.defaultPackage);
        this.setDefaultPackage();
      }),
      tap(value => {
        const filterObject = utils.groupByKey((value?.generalPackages ?? []), 'groupName');
        const subOptions = Object.keys((filterObject || [])).map((key) => {
          return ({
            groupLabel: key,
            options: (filterObject[key] || []).map((item) => {
              return ({
                label: `${item.name.toUpperCase()} - ${this.currencyPipe.transform(
                  item.baseFee,
                  item?.currency || 'HUF'
                )} + ${this.translate.instant('VAT')}`,
                value: item,
                id: item.name.toUpperCase()
              });
            })
          });
        });
        this.subscriptionOptions.set(subOptions);
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.packages$.pipe(
      tap(value => {
        const subOptions = (value?.storagePackages || []).map((item) => {
          return ({
            label: `${item.name.toUpperCase()} - ${this.currencyPipe.transform(
              item.fee,
              item?.currency || 'HUF'
            )} + ${this.translate.instant('VAT')}`,
            value: item,
            id: item.name.toUpperCase()
          });
        });

        this.storageSubscriptionOptions.set(subOptions);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public setDefaultPackage(){
    if(!!this.useDefaultPackage  && this.registrationForm){
      const generalPackageControl = this.registrationForm.get('generalPackage');
      generalPackageControl.setValue(this.useDefaultPackage);
    }
  }

  public setForm(form: UntypedFormGroup): void {
    this.registrationForm = form;
    const generalPackageControl = this.registrationForm.get('generalPackage');
    const userCountControl = this.registrationForm.get('userCount');
    generalPackageControl.reset();

    this.setDefaultPackage();
    userCountControl.setValue(0);
  }

  public submitForm(): void {
    if (!this.registrationForm || this.isLoading$.getValue()) {
      return;
    }
    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();
      return;
    }
    if (this.recaptchaControl.invalid) {
      this.recaptchaControl.markAllAsTouched();
      return;
    }
    const registrationData = utils.omit(this.registrationForm.getRawValue(), [
      'confirmPassword',
      this.registrationForm.value['whereToSee'] !== 'other'? 'otherDescription' : '',
      'documentNumber',
      'userCount'
    ]);

    if(registrationData.companies.length > registrationData.generalPackage.maxPartnerInGroup){
      this.registrationForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    const token = this.recaptchaControl.value;
    const registrationObject = {
      token,
      ...registrationData,
      generalPackage: registrationData.generalPackage.id
    };
    this.registerUser(registrationObject);
  }

  private registerUser(registrationData: any) {
    this.authSandbox.registration(registrationData)
      .subscribe({
        next: () => {
          this.email = registrationData.email;
          this.isLoading$.next(false);
          this.showSuccessScreen = true;
        },
        error: () => {
          this.isLoading$.next(false);
        },
        complete: () => {
          this.email = registrationData.email;
          this.isLoading$.next(false);
          this.showSuccessScreen = true;
        }
      });
  }

  public goToLogin(): void {
    this.navigation.navigate([RoutesEnum.LOGIN]);
  }
}
